import React, { useState, useEffect } from 'react';
import { reviewService } from '../services/api.service';
import { getProductImageUrl } from '../../services/api';
import {
  Star,
  Search,
  RefreshCw,
  Trash2,
  RotateCcw,
  CheckCircle2,
  Eye,
  ChevronLeft,
  ChevronRight,
  Filter,
  Image as ImageIcon,
  User,
  MessageSquare
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'deleted', 'all'
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const res = await reviewService.getReviews();
      setReviews(res.reviews || []);
    } catch (err) {
      console.warn("Failed to load admin reviews:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const handleSoftDelete = async (id, shouldDelete) => {
    try {
      await reviewService.softDeleteReview(id, shouldDelete);
      setReviews(prev => prev.map(r => r.id === id ? { ...r, is_deleted: shouldDelete } : r));
      if (selectedReview && selectedReview.id === id) {
        setSelectedReview(prev => ({ ...prev, is_deleted: shouldDelete }));
      }
    } catch (err) {
      alert("Failed to update review status.");
    }
  };

  // Metrics Calculations
  const totalReviews = reviews.length;
  const activeReviews = reviews.filter(r => !r.is_deleted);
  const deletedCount = reviews.filter(r => r.is_deleted).length;
  const avgRating = activeReviews.length > 0
    ? (activeReviews.reduce((sum, r) => sum + parseFloat(r.rating || 4), 0) / activeReviews.length).toFixed(1)
    : '4.0';

  // Filtering Logic
  const filteredReviews = reviews.filter(r => {
    const term = search.toLowerCase();
    const productName = (r.product_name || '').toLowerCase();
    const customerName = (r.user_name || '').toLowerCase();
    const comment = (r.comment || '').toLowerCase();

    const matchesSearch = productName.includes(term) || customerName.includes(term) || comment.includes(term);

    let matchesStatus = true;
    if (statusFilter === 'active') matchesStatus = !r.is_deleted;
    if (statusFilter === 'deleted') matchesStatus = r.is_deleted;

    return matchesSearch && matchesStatus;
  });

  // Pagination Math
  const totalPages = Math.ceil(filteredReviews.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentReviews = filteredReviews.slice(indexOfFirstItem, indexOfLastItem);

  const renderStars = (rating) => {
    const num = Math.round(parseFloat(rating || 4));
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '2px', color: '#f59e0b' }}>
        {[...Array(5)].map((_, i) => (
          <Star key={i} size={14} fill={i < num ? '#f59e0b' : 'none'} color="#f59e0b" />
        ))}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Banner & Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            Product Reviews & Moderation ({totalReviews} Reviews)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Inspect customer ratings, review uploaded photo proof, and soft-delete inappropriate content.
          </p>
        </div>

        <button onClick={fetchReviews} className="btn-secondary" style={{ width: 'auto', padding: '0 16px', height: '40px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} />
          <span>Refresh Reviews</span>
        </button>
      </div>

      {/* METRICS OVERVIEW CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Average Store Rating</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fffbeb', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Star size={16} fill="#f59e0b" />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{avgRating} <span style={{ fontSize: '16px', color: '#64748b' }}>/ 5.0</span></div>
          <div style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 700, marginTop: '4px' }}>Default Baseline: 4.0 Stars</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Reviews</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{activeReviews.length}</div>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginTop: '4px' }}>Published Storefront Reviews</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Soft-Deleted</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Trash2 size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{deletedCount}</div>
          <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px' }}>Hidden From Storefront</div>
        </div>

      </div>

      {/* FILTER & SEARCH BAR */}
      <div className="table-card-box" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          
          <div className="header-search-wrapper" style={{ width: '360px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search by product, customer name or review text..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Filter size={14} color="#64748b" />
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: 700, outline: 'none' }}
            >
              <option value="active">Active Storefront Reviews</option>
              <option value="deleted">Soft-Deleted Reviews</option>
              <option value="all">All Reviews</option>
            </select>
          </div>

        </div>

        {/* REVIEWS TABLE */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>PRODUCT</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>CUSTOMER</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>RATING</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>COMMENT & PHOTOS</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>STATUS</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>DATE</th>
                <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading customer reviews...</td></tr>
              ) : currentReviews.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No reviews found.</td></tr>
              ) : (
                currentReviews.map((r) => {
                  const imagesList = Array.isArray(r.images) ? r.images : [];
                  const hasPhotos = imagesList.length > 0;

                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', background: r.is_deleted ? '#fff5f5' : '#ffffff' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 800, color: '#0f172a' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '36px', height: '36px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2px', flexShrink: 0 }}>
                            <img src={getProductImageUrl(r.product_image)} alt="Product" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                          </div>
                          <span>{r.product_name || `Product #${r.product_id}`}</span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{r.user_name}</div>
                        {r.user_email && <div style={{ fontSize: '11.5px', color: '#64748b' }}>{r.user_email}</div>}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {renderStars(r.rating)}
                        <span style={{ fontSize: '11.5px', fontWeight: 800, color: '#0f172a', marginTop: '2px', display: 'block' }}>{parseFloat(r.rating).toFixed(1)} / 5.0</span>
                      </td>
                      <td style={{ padding: '14px 16px', maxWidth: '320px' }}>
                        {r.title && <div style={{ fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>{r.title}</div>}
                        <div style={{ color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.comment}</div>
                        {hasPhotos && (
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 800, color: '#dc2626', background: '#fef2f2', padding: '2px 8px', borderRadius: '4px', marginTop: '4px' }}>
                            <ImageIcon size={12} />
                            <span>{imagesList.length} {imagesList.length === 1 ? 'Photo' : 'Photos'}</span>
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {r.is_deleted ? (
                          <span style={{ fontSize: '11px', fontWeight: 800, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', padding: '3px 8px', borderRadius: '6px' }}>
                            Soft-Deleted
                          </span>
                        ) : (
                          <span style={{ fontSize: '11px', fontWeight: 800, color: '#15803d', background: '#dcfce7', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: '6px' }}>
                            Active
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '12px' }}>
                        {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
                          <button
                            onClick={() => setSelectedReview(r)}
                            style={{ background: '#f8fafc', border: '1px solid #cbd5e1', color: '#0f172a', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                          >
                            <Eye size={14} />
                            <span>View</span>
                          </button>

                          {r.is_deleted ? (
                            <button
                              onClick={() => handleSoftDelete(r.id, false)}
                              style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#059669', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <RotateCcw size={14} />
                              <span>Restore</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSoftDelete(r.id, true)}
                              style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '6px 10px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                            >
                              <Trash2 size={14} />
                              <span>Soft Delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* VIEW REVIEW MODAL */}
      {selectedReview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '640px', padding: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: '0 0 2px 0' }}>
                  Review Details - {selectedReview.product_name}
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Submitted by {selectedReview.user_name} on {new Date(selectedReview.created_at).toLocaleDateString()}
                </span>
              </div>
              <button onClick={() => setSelectedReview(null)} style={{ background: 'none', border: 'none', fontSize: '18px', fontWeight: 800, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '13.5px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <strong style={{ color: '#0f172a' }}>Rating:</strong>
                {renderStars(selectedReview.rating)}
                <span style={{ fontWeight: 800, color: '#0f172a' }}>{parseFloat(selectedReview.rating).toFixed(1)} / 5.0</span>
              </div>

              {selectedReview.title && (
                <div><strong style={{ color: '#0f172a' }}>Headline:</strong> {selectedReview.title}</div>
              )}

              <div>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '6px' }}>Review Comment:</strong>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {selectedReview.comment}
                </div>
              </div>

              {/* Uploaded Photos Gallery */}
              {Array.isArray(selectedReview.images) && selectedReview.images.length > 0 && (
                <div>
                  <strong style={{ color: '#0f172a', display: 'block', marginBottom: '8px' }}>Customer Attached Photos ({selectedReview.images.length}):</strong>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {selectedReview.images.map((img, idx) => (
                      <a key={idx} href={getProductImageUrl(img)} target="_blank" rel="noreferrer" style={{ width: '80px', height: '80px', borderRadius: '10px', border: '1px solid #cbd5e1', overflow: 'hidden', display: 'block' }}>
                        <img src={getProductImageUrl(img)} alt={`Review photo ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
              {selectedReview.is_deleted ? (
                <button
                  onClick={() => handleSoftDelete(selectedReview.id, false)}
                  style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#10b981', color: '#ffffff', fontWeight: 800, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                >
                  <RotateCcw size={16} />
                  <span>Restore to Storefront</span>
                </button>
              ) : (
                <button
                  onClick={() => handleSoftDelete(selectedReview.id, true)}
                  style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#dc2626', color: '#ffffff', fontWeight: 800, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                >
                  <Trash2 size={16} />
                  <span>Soft Delete from Storefront</span>
                </button>
              )}
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default Reviews;
